import { ActiveIngredientCalculatedQuantity } from '@dreryk/egabinet-prescription-frontend-api';

import { assertUnreachable, isEmpty } from '~libs.typescript';

import { MedicinePrescription } from '../types';
import { isMagistralDrugMedicinePrescription } from '../guards/isMagistralDrugMedicinePrescription';
import { isStandardMedicinePrescription } from '../guards/isStandardMedicinePrescription';

import { hasRpwMagistralDrugInMedicineRawMaterials } from './magistralDrug';

const NO_ACTIVE_INGREDIENT_CALCULATED_QUANTITIES: ActiveIngredientCalculatedQuantity[] = [];
const QUANTITY_PRECISION = 6;

export const createActiveIngredientQuantityFormatter = (withName: boolean) => ({
   name,
   quantity,
   unitName,
}: ActiveIngredientCalculatedQuantity): string => {
   const ingredientName = withName ? name : '';
   const amount = `${quantity ?? ''}${unitName ?? ''}`;
   if (!quantity) {
      if (!withName) {
         return '';
      }

      return ingredientName;
   }

   return `${ingredientName} ${amount}`;
};

const preciseQuantity = (quantity: number): number => {
   return Number(quantity.toFixed(QUANTITY_PRECISION));
};

export const getActiveIngredientCalculatedQuantites = (
   medicinePrescription: MedicinePrescription,
   medicinePackageQuantity: number | null | undefined
): ActiveIngredientCalculatedQuantity[] => {
   if (isEmpty(medicinePrescription.activeIngredientQuantities) || isEmpty(medicinePackageQuantity)) {
      return NO_ACTIVE_INGREDIENT_CALCULATED_QUANTITIES;
   }

   if (isStandardMedicinePrescription(medicinePrescription)) {
      const { medicine } = medicinePrescription.patientMedicine;

      if (!medicine.narcoticOrPsychotropic) {
         return NO_ACTIVE_INGREDIENT_CALCULATED_QUANTITIES;
      }

      return medicinePrescription.activeIngredientQuantities?.reduce((result, { activeIngredient }) => {
         if (!isEmpty(activeIngredient.amountPerItem) && !isEmpty(medicine.singlePackageCapacity)) {
            result.push({
               name: activeIngredient.name,
               quantity: preciseQuantity(
                  activeIngredient.amountPerItem * medicine.singlePackageCapacity * medicinePackageQuantity
               ),
               unitName: activeIngredient.unitName,
            });
         }

         return result;
      }, [] as ActiveIngredientCalculatedQuantity[]);
   }

   if (isMagistralDrugMedicinePrescription(medicinePrescription)) {
      if (
         hasRpwMagistralDrugInMedicineRawMaterials(medicinePrescription.magistralDrug.magistralDrugMedicineRawMaterials)
      ) {
         return NO_ACTIVE_INGREDIENT_CALCULATED_QUANTITIES;
      }

      const { magistralDrugMedicineRawMaterials } = medicinePrescription.magistralDrug;

      const hasRawMaterialWithoutQuantity = magistralDrugMedicineRawMaterials.some(
         ({ quantity, quantityUnit }) => isEmpty(quantity) || isEmpty(quantityUnit)
      );
      if (hasRawMaterialWithoutQuantity) {
         return NO_ACTIVE_INGREDIENT_CALCULATED_QUANTITIES;
      }

      return magistralDrugMedicineRawMaterials.reduce((result, { name, quantityUnit, quantity }) => {
         if (isEmpty(quantity) || isEmpty(quantityUnit)) {
            return result;
         }

         result.push({
            name: name,
            quantity: preciseQuantity(quantity * medicinePackageQuantity),
            unitName: quantityUnit,
         });

         return result;
      }, [] as ActiveIngredientCalculatedQuantity[]);
   }

   return assertUnreachable();
};
